import { faExternalLinkSquare } from "@fortawesome/free-solid-svg-icons";
import {
  ANONYMOUS,
  GSImageEditor,
  GSSelect,
  GSRadioGroup,
  defaultLayouts,
  validations,
} from "golfstatus_react_components";
import PackageLineItem from "../components/events/event/packages/package-line-item";
import SponsorLogoSelect from "../components/widgets/sponsor-logo-select";
import MobileSponsorLogoSelect from "../components/widgets/mobile-sponsor-logo-select";
import { openExternalLink } from "../helpers/Utilities";

import { PLAYER_FORM } from "../components/events/event/cart/order-item-form";
import HoleAssignmentSelectWidget from "../components/widgets/hole-assignment-select-widget";
import { registrationCustomerComplete } from "../models/Registration";

export const tees = [
  { label: "Men's Tees", value: "mens_tees" },
  { label: "Women's Tees", value: "womens_tees" },
];
export const teeLocations = [
  { label: "Default Tees", value: "default_tees" },
  { label: "Back Tees", value: "back_tees" },
  { label: "Front Tees", value: "forward_tees" },
];

export const GHIN_NUMBER_TYPE = { label: "GHIN Number", value: 1 };
export const HANDICAP_TYPE = { label: "Handicap", value: 2 };
export const handicapTypes = [GHIN_NUMBER_TYPE, HANDICAP_TYPE];

export const MIN_HANDICAP_INDEX = -20.0;
export const MAX_HANDICAP_INDEX = 54.0;

export const LOW_HANDICAP_INDEX_LABEL =
  "The lowest handicap index is +20.0 for all players";
export const HIGH_HANDICAP_INDEX_LABEL =
  "The highest handicap index is 54.0 for all players";
export const INVALID_HANDICAP_INDEX_LABEL =
  "Handicap index must be a number.";

/**
 * parses a handicap index entry
 *
 * @param {string} value entered handicap index
 * @returns {number} the parsed index, undefined when empty, NaN when not a number
 */
const parseHandicapIndex = (value) => {
  const entry = `${value ?? ""}`.trim();
  if (entry === "") {
    return undefined;
  }
  return Number(entry);
};

/**
 * a handicap index must be a number between MIN_HANDICAP_INDEX and
 * MAX_HANDICAP_INDEX. empty entries are left to the required check.
 *
 * @param {string} value entered handicap index
 * @returns {boolean} entry is a valid handicap index
 */
export const validHandicapIndex = (value) => {
  const index = parseHandicapIndex(value);
  if (index === undefined) {
    return true;
  }
  return (
    Number.isFinite(index) &&
    index >= MIN_HANDICAP_INDEX &&
    index <= MAX_HANDICAP_INDEX
  );
};

/**
 * gs-field reads invalidLabel as a static value, so the message for the
 * current entry is resolved as the field is built.
 *
 * @param {string} value entered handicap index
 * @returns {string} message describing why the entry is invalid
 */
export const getHandicapIndexInvalidLabel = (value) => {
  const index = parseHandicapIndex(value);
  if (!Number.isFinite(index)) {
    return INVALID_HANDICAP_INDEX_LABEL;
  }
  if (index < MIN_HANDICAP_INDEX) {
    return LOW_HANDICAP_INDEX_LABEL;
  }
  return HIGH_HANDICAP_INDEX_LABEL;
};

export const MIN_GHIN_DIGITS = 6;
export const MAX_GHIN_DIGITS = 8;

export const SHORT_GHIN_NUMBER_LABEL =
  "GHIN numbers must be at least 6 digits";
export const LONG_GHIN_NUMBER_LABEL =
  "GHIN numbers must be no more than 8 digits";
export const NON_NUMERIC_GHIN_NUMBER_LABEL =
  "GHIN numbers must contain only digits";

const GHIN_NUMBER_PATTERN = /^\d+$/;

/**
 * trims a ghin number entry so validation and its message agree on the entry
 *
 * @param {string} value entered ghin number
 * @returns {string} the trimmed entry
 */
const parseGhinNumber = (value) => `${value ?? ""}`.trim();

/**
 * a ghin number is entered without dashes, so it is digits only and between
 * MIN_GHIN_DIGITS and MAX_GHIN_DIGITS of them. empty entries are left to the
 * required check.
 *
 * @param {string} value entered ghin number
 * @returns {boolean} entry is a valid ghin number
 */
export const validGhinNumber = (value) => {
  const entry = parseGhinNumber(value);
  if (entry === "") {
    return true;
  }
  return (
    GHIN_NUMBER_PATTERN.test(entry) &&
    entry.length >= MIN_GHIN_DIGITS &&
    entry.length <= MAX_GHIN_DIGITS
  );
};

/**
 * gs-field reads invalidLabel as a static value, so the message for the
 * current entry is resolved as the field is built. a non numeric entry is
 * reported before its length, since the stray characters are what need fixing.
 *
 * @param {string} value entered ghin number
 * @returns {string} message describing why the entry is invalid
 */
export const getGhinNumberInvalidLabel = (value) => {
  const entry = parseGhinNumber(value);
  if (!GHIN_NUMBER_PATTERN.test(entry)) {
    return NON_NUMERIC_GHIN_NUMBER_LABEL;
  }
  if (entry.length > MAX_GHIN_DIGITS) {
    return LONG_GHIN_NUMBER_LABEL;
  }
  return SHORT_GHIN_NUMBER_LABEL;
};


export const getPackageForm = (context) => {
  const packageSection = {
    fields: [
      {
        style: { ...defaultLayouts.fullWidth },
        value: (
          <PackageLineItem
            style={{
              ...defaultLayouts.fullWidth,
            }}
            headerType="headline-1"
            {...context?.getData?.()}
            action={context.getPackageAction()}
          />
        ),
      },
    ],
  };

  const form = [];

  form.unshift?.(packageSection);

  return form;
};

/**
 *
 * @param {object} context
 * @returns {Array} Form sections array
 *
 */
export const getPlayerForm = (context) => {
  const players = context?.getPlayers?.();

  const playersForms =
    players?.flatMap?.((p, index) => {
      return playerInformationSection(context, p, index);
    }) ?? [];

  const form = [...playersForms];

  return form;
};

/**
 *
 * @param {object} context
 * @returns {Array} Form sections array
 *
 */
export const getTeamForm = (context) => {
  const teams = context?.getTeams?.();

  const teamsForms =
    teams?.flatMap?.((t) => {
      return teamInformationSections(t, context);
    }) ?? [];

  const form = [...teamsForms];

  return form;
};

/**
 *
 * @param {object} context
 * @returns {Array} Form sections array
 *
 */
export const getSponsorForm = (context) => {
  const sponsors = context?.getSponsorData?.();

  const sponsorForms =
    sponsors?.flatMap?.((s) => {
      return sponsorInformationSections(s, context, s.id);
    }) ?? [];

  const form = [...sponsorForms];

  return form;
};

/**
 *
 * @param {object} context
 * @returns {Array} Form sections array
 *
 */
export const getOrderForm = (context) => {
  const forms = [...getCustomFieldSets(context?.getData?.(), context)];

  return [
    {
      type: "vertical xx-large-gap",
      gridColumns: 3,
      fields: forms.flatMap?.((pf) => pf),
    },
  ];
};

/**
 *
 * @param {InProgressTeam} team
 * @param {object} context
 * @returns
 */
const teamInformationSections = (team, context) => {
  let sections =
    team.inProgressPlayers?.map?.((p, index) => {
      return playerInformationSection(context, p, index);
    }) ?? [];

  if (team?.customFieldResponses?.length > 0) {
    sections.push(additionalInformationSection(team, context, team.id));
  }

  if (team.inProgressTournamentTeamsRoundHoleAssignments?.length > 0) {
    const s = team.inProgressTournamentTeamsRoundHoleAssignments?.map?.(
      (trha) => {
        return {
          type: "vertical xx-large-gap",
          fields: [
            {
              label: `Tee Time`,
              value: (
                <HoleAssignmentSelectWidget
                  holeAssignment={trha}
                  onChange={(option) => {
                    const assignments = [
                      ...(team.inProgressTournamentTeamsRoundHoleAssignments ??
                        []),
                    ];
                    const index = assignments?.findIndex?.(
                      (ha) => ha.id === trha.id
                    );
                    if (index !== -1) {
                      assignments[index] = {
                        ...trha,
                        roundHoleAssignment: option.value,
                      };
                      context?.updateTeamData?.(
                        assignments,
                        "inProgressTournamentTeamsRoundHoleAssignments",
                        team.id
                      );
                    }
                  }}
                />
              ),
              isEditable: true,
              customView: true,
              required: true,
              hintText: "Please select your preferred tee time.",
              failedValidation: context.validationFailed,
              validation: {
                isValid: () => {
                  const missingHA = team?.inProgressTournamentTeamsRoundHoleAssignments?.some?.(
                    (ha) => ha?.roundHoleAssignment === null
                  );
                  return !missingHA;
                },
                invalidLabel: "Hole assignment selection is required.",
              },
            },
          ],
        };
      }
    );
    sections = [...s, ...sections];
  }

  return sections;
};

/**
 *
 * @param {InProgressSponsor} sponsor
 * @param {object} context
 * @returns
 */
const sponsorInformationSections = (sponsor, context) => {
  let sections = [
    sponsorInformationSection(context, sponsor.id),
    sponsorImagesSection(context, sponsor.id),
    sponsorInformationContactSection(context, sponsor.id),
  ];

  if (sponsor?.customFieldResponses?.length > 0) {
    sections.push(additionalInformationSection(sponsor, context, sponsor.id));
  }

  return sections;
};

/**
 *
 * @param {object} section the section that is holding the custom field responses [inProgressTeam, inProgressSponsor]
 * @param {object} context
 * @returns
 */
const additionalInformationSection = (section, context, id) => {
  const forms = [
    ...getCustomFieldSets(section.customFieldResponses, context, id),
  ];

  return {
    title: "Additional Information",
    type: "vertical xx-large-gap",
    gridColumns: 3,
    fields: forms.flatMap?.((pf) => pf),
  };
};

/**
 *
 * @param {object} context
 * @param {InProgressPlayer} inProgressPlayer
 * @returns
 */
const playerInformationSection = (context, inProgressPlayer, index) => {
  const nonContactFields = [
    {
      label: "First Name",
      isEditable: true,
      placeholder: "First Name",
      ...context.setBinding("firstName", inProgressPlayer?.id),
    },
    {
      label: "Last Name",
      isEditable: true,
      placeholder: "Last Name",
      ...context.setBinding("lastName", inProgressPlayer?.id),
    },
    {
      label: "Email Address",
      isEditable: true,
      placeholder: "Email Address",
      ...context.setBinding("email", inProgressPlayer?.id),
      type: "email",
      validation: {
        isValid: validations.validEmail,
        invalidLabel: "Must be a valid email.",
      },
    },
    {
      label: "Phone Number",
      isEditable: true,
      placeholder: "Phone Number",
      ...context.setBinding("phone", inProgressPlayer?.id),
      validation: {
        isValid: validations.validPhone,
        invalidLabel: "Must be a valid phone.",
      },
    },
  ];

  const contactFields = [
    { value: context?.contactDetails?.firstName, label: "First Name" },
    { value: context?.contactDetails?.lastName, label: "Last Name" },
    { value: context?.contactDetails?.email, label: "email Address" },
    { value: context?.contactDetails?.phone, label: "phone number" },
  ];

  const canUseContactDetails =
    inProgressPlayer?.useRegistrationDetails &&
    registrationCustomerComplete(context?.contactDetails);

  const fields = canUseContactDetails ? contactFields : nonContactFields;

  if (context?.tournament?.handicapScoring) {
    const handicapType =
      context?.getHandicapType?.(inProgressPlayer) ?? GHIN_NUMBER_TYPE;

    const isHandicapEntry = handicapType?.value === HANDICAP_TYPE.value;

    const handicapBinding = isHandicapEntry
      ? context.setBinding("handicap", inProgressPlayer?.id)
      : context.setBinding("ghinNumber", inProgressPlayer?.id);

    const handicapField = isHandicapEntry
      ? {
          label: "Handicap",
          isEditable: true,
          placeholder: "Handicap",
          hintText: "For plus handicaps, enter as a negative number.",
          ...handicapBinding,
          validation: {
            isValid: validHandicapIndex,
            invalidLabel: getHandicapIndexInvalidLabel(handicapBinding?.value),
          },
        }
      : {
          label: "GHIN Number",
          isEditable: true,
          placeholder: "GHIN Number",
          ...handicapBinding,
          hintText: "Enter GHIN number without dashes.",
          validation: {
            isValid: validGhinNumber,
            invalidLabel: getGhinNumberInvalidLabel(handicapBinding?.value),
          },
        };

    fields.push(
      {
        label: "Handicap Type",
        isEditable: true,
        customView: true,
        value: (
          <GSRadioGroup
            options={handicapTypes}
            selectedOption={handicapType}
            selectionChanged={(option) =>
              context?.setHandicapType?.(inProgressPlayer, option)
            }
            isLtr={true}
            //is-ltr pads the group, which indents the first button past the label
            style={{ paddingLeft: 0 }}
          />
        ),
      },
      handicapField,
      {
        label: "Select A Tee",
        value: (
          <GSSelect
            options={tees}
            placeholder="Select A Tee..."
            selectedOption={tees?.find?.(
              (t) => t.value === inProgressPlayer?.teeValue
            )}
            onChange={(option) => {
              context?.updateTeamPlayerData?.(
                option.value,
                "teeValue",
                inProgressPlayer?.id
              );
            }}
          />
        ),
      },
      {
        label: "Select A Tee Location",
        value: (
          <GSSelect
            options={teeLocations}
            placeholder="Select A Tee Location..."
            selectedOption={teeLocations?.find?.(
              (t) => t.value === inProgressPlayer?.teePreference
            )}
            onChange={(option) => {
              context?.updateTeamPlayerData?.(
                option.value,
                "teePreference",
                inProgressPlayer?.id
              );
            }}
          />
        ),
      }
    );
  }

  fields.push({
    label: "Player Notes",
    isEditable: true,
    placeholder: "Player Notes",
    type: "text-area",
    ...context.setBinding("signupMessage", inProgressPlayer?.id),
  });

  if (inProgressPlayer?.customFieldResponses?.length > 0) {
    fields.push(
      ...getCustomFieldSets(
        inProgressPlayer.customFieldResponses,
        context,
        inProgressPlayer.id,
        PLAYER_FORM
      )
    );
  }

  return {
    title: `Player ${index + 1} Details`,
    sectionActions: [
      {
        actionType: "toggle",
        pageActionProps: {
          value: canUseContactDetails,
          descriptionStyle: { textWrap: "noWrap" },
          rowReverse: true,
          falseDescription: "Use Contact Details",
          trueDescription: "Use Contact Details",
          type: "no-wrap",
          onClick: (value) => {
            context.updateTeamPlayerData(
              !inProgressPlayer?.useRegistrationDetails,
              "useRegistrationDetails",
              inProgressPlayer?.id
            );
          },
        },
      },
    ],
    type: "vertical xx-large-gap mobile-vertical",
    gridColumns: 2,
    fields: fields,
  };
};

const getCustomFieldSets = (customFieldResponses, context, id, formType) => {
  const additionalFields = [...(customFieldResponses ?? [])]

  additionalFields?.sort?.((a,b) => a.registrationCustomField.position - b.registrationCustomField.position);

  const additionalForms =
    additionalFields?.map?.((field, index) => {
      let formField = {
        style: { gridColumn: "1 / span 2" },
        label: ` ${field?.registrationCustomField?.name} `,
        isEditable: true,
        placeholder:
          field?.registrationCustomField?.placeholder ??
          field?.registrationCustomField?.name,
        value: field?.value,
        onChange: (e) => {
          let update = { ...field };
          update.value = e.target?.value;
          context?.updateData?.(update, index, id, formType);
        },
        failedValidation: context?.validationFailed,
        required: field?.registrationCustomField?.required,
      };
      if (field?.registrationCustomField?.options?.length > 0) {
        formField.value = (
          <GSSelect
            options={field?.registrationCustomField?.options?.map?.(
              (option) => {
                return { label: option, value: option };
              }
            )}
            selectedOption={
              (field?.value ?? "") !== "" ? { label: field?.value } : null
            }
            placeholder={
              field?.registrationCustomField?.placeholder ??
              field?.registrationCustomField?.name
            }
            onChange={(option) => {
              let update = { ...field };
              update.value = option.value;
              context?.updateData?.(update, index, id, formType);
            }}
          />
        );
        formField.validation = {
          isValid: () => {
            const valid =
              !field?.registrationCustomField?.required ||
              (field?.value ?? "") !== "";
            return valid;
          },
          invalidLabel: "This field is required",
        };
        formField.isEditable = true;
        formField.customView = true;
        formField.failedValidation = context?.validationFailed;
        formField.required = field?.registrationCustomField?.required;
      }
      return formField;
    }) ?? [];

  return additionalForms;
};

// const fieldSetSection = (context) => {
//   const fieldSets = context?.getData?.().fieldSets;
//   const fields = fieldSets?.flatMap?.((fs) => fs?.fieldSets?.fields);
//   return {
//     title: "Additional Information",
//     type: "vertical xx-large-gap",
//     fields: fields?.map?.((f) => {
//       return { label: f?.name, required: f?.required, isEditable: true };
//     }),
//   };
// };

const sponsorInformationSection = (context, id) => {
  const website = context?.getSponsorData?.(id)?.website ?? "";
  return {
    type: "vertical xx-large-gap",
    fields: [
      {
        label: "Sponsor Name",
        isEditable: true,
        required: true,
        placeholder: "Sponsor Name",
        ...context?.setSponsorBinding?.("name", id),
      },
      {
        label: "Sponsor Website",
        isEditable: true,
        placeholder: "Sponsor Website",
        hintText: "e.g. www.sponsorwebsite.com",
        rightIcon: faExternalLinkSquare,
        rightIconClick: () => {
          if (website !== "") {
            openExternalLink(website);
          }
        },
        validation: {
          isValid: validations.validLink,
          invalidLabel: "Must be a valid link",
        },
        onBlur: () => {
          if(!website?.toLowerCase().startsWith?.("http") && !website?.toLowerCase()?.startsWith?.("www.") && website !== ""){
            context?.updateSponsorData?.(`www.${website}`, "website", id);
          }
        },
        ...context?.setSponsorBinding?.("website", id),
      },
      {
        label: "Sponsor Message",
        isEditable: true,
        type: "text-area",
        rows: 5,
        placeholder: "Sponsor Message",
        hintText:
          "This message will be shown in the sponsor details section of GolfStatus app.",
        ...context?.setSponsorBinding?.("message", id),
      },
    ],
  };
};

const sponsorImagesSection = (context, id) => {
  return {
    type: "vertical xx-large-gap",
    title: "Sponsor Images",
    fields: [
      {
        label: "Sponsor Image",
        value: (
          <SponsorLogoSelect
            sponsorID={id}
            context={{
              getData: () => context?.getSponsorData?.(id),
              updateData: (file, property) => {
                context?.updateSponsorData(file, property, id);
              },
              validationFailed: context.validationFailed,
            }}
          />
        ),
      },
      {
        label: "In-App Sponsor Image",
        value: (
          <MobileSponsorLogoSelect
            sponsorID={id}
            context={{
              getData: () => context?.getSponsorData?.(id),
              updateData: (file, property) => {
                context?.updateSponsorData(file, property, id);
              },
              validationFailed: context.validationFailed,
            }}
          />
        ),
      },
    ],
  };
};

const sponsorInformationContactSection = (context, id) => {
  return {
    title: "Sponsor Contact",
    type: "vertical xx-large-gap",
    fields: [
      {
        label: "Full Name",
        isEditable: true,
        placeholder: "Full Name",
        ...context?.setSponsorBinding?.("contactName", id),
      },
      {
        label: "Email Address",
        isEditable: true,
        placeholder: "Email Address",
        ...context?.setSponsorBinding?.("email", id),
        type:"email",
        validation: {
          isValid: validations.validEmail,
          invalidLabel: "Must be a valid email.",
        },
      },
      {
        label: "Phone Number",
        isEditable: true,
        placeholder: "Phone Number",
        ...context?.setSponsorBinding?.("phone", id),
        validation: {
          isValid: validations.validPhone,
          invalidLabel: "Must be a valid phone",
        },
      },
    ],
  };
};

/**
 *
 * @param {object} context
 * @returns {Array} Form sections array
 *
 */
export const getCustomerForm = (context) => {
  const form = [
    {
      type: "vertical xx-large-gap",
      fields: [
        {
          label: "First Name",
          isEditable: true,
          placeholder: "First Name",
          required: true,
          ...context.setBinding?.("firstName"),
        },
        {
          label: "Last Name",
          isEditable: true,
          placeholder: "Last Name",
          required: true,
          ...context.setBinding?.("lastName"),
        },
        {
          label: "Email Address",
          isEditable: true,
          placeholder: "Email Address",
          required: true,
          ...context.setBinding?.("email"),
          type: "email",
          validation: {
            isValid: validations.validEmail,
            invalidLabel: "Must be a valid email.",
          },
        },
        {
          label: "Phone Number",
          isEditable: true,
          placeholder: "Phone Number",
          required: true,
          ...context.setBinding?.("phone"),
          validation: {
            isValid: validations.validPhone,
            invalidLabel: "Must be a valid phone number.",
          },
        },
      ],
    },
  ];

  return form;
};

export const getImageCropForm = (context, showCroppedImage, croppedImg) => {
  return [
    {
      type: "vertical",
      fields: [
        {
          value: showCroppedImage ? (
            <img className="preview-image" src={croppedImg} alt="upload" />
          ) : (
            <GSImageEditor
              source={context?.getLogo?.()}
              minWidth={600}
              minHeight={context.getMinHeight?.()}
              setCroppedDataURL={context.setCroppedDataURL}
              setCroppedBlob={context.setCroppedBlob}
              resizeEnabled={true}
              ratio={context?.getRatio?.()}
              crossOrigin={ANONYMOUS}
            ></GSImageEditor>
          ),
        },
      ],
    },
  ];
};
